import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import adminService from '../../../services/adminService';

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 30 * 86400000);
  return {
    dateFrom: start.toISOString().slice(0, 10),
    dateTo: end.toISOString().slice(0, 10),
  };
}

function userProfilePath(userId, userType) {
  const t = (userType || '').toLowerCase();
  if (t === 'airliner') return `/admin/airliners/${userId}`;
  if (t === 'jetsetter') return `/admin/jetsetters/${userId}`;
  return `/admin/users/${userId}`;
}

const BookingManagementPage = () => {
  const [form, setForm] = useState({
    dateFrom: '',
    dateTo: '',
    booking_status: '',
    transaction_status: '',
    aircraft_id: '',
    search: '',
  });
  const [applied, setApplied] = useState({
    dateFrom: '',
    dateTo: '',
    booking_status: '',
    transaction_status: '',
    aircraft_id: '',
    search: '',
  });
  const [aircraftLoading, setAircraftLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(25);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [aircraft, setAircraft] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit,
        dateFrom: applied.dateFrom,
        dateTo: applied.dateTo,
        booking_status: applied.booking_status || undefined,
        transaction_status: applied.transaction_status || undefined,
        aircraft_id: applied.aircraft_id || undefined,
        search: applied.search || undefined,
      };
      const res = await adminService.getAdminBookings(params);
      if (res.success) {
        setPayload(res.data);
      } else {
        toast.error(res.message || 'Failed to load bookings');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, applied]);

  // Load aircraft list on mount
  useEffect(() => {
    const loadAircraft = async () => {
      setAircraftLoading(true);
      try {
        console.log('[BookingManagementPage] Loading aircraft list...');
        const res = await adminService.getAircraftList();
        console.log('[BookingManagementPage] Aircraft response:', res);
        if (res.success) {
          console.log('[BookingManagementPage] Aircraft data:', res.data);
          setAircraft(res.data);
        }
      } catch (e) {
        console.error('Failed to load aircraft:', e);
      } finally {
        setAircraftLoading(false);
      }
    };
    loadAircraft();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleApply = (e) => {
    e.preventDefault();
    setApplied({ ...form });
    setCurrentPage(1);
  };

  const handleResetDates = () => {
    const d = defaultDateRange();
    setForm((f) => ({ ...f, ...d }));
    setApplied((a) => ({ ...a, ...d }));
    setCurrentPage(1);
  };

  const summary = payload?.summary;
  const meta = payload?.meta;
  const pagination = payload?.pagination;
  const bookings = payload?.bookings || [];
  const totalPages = pagination?.total_pages || 1;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Booking Management</h1>
        </div>

        <form
          onSubmit={handleApply}
          className="bg-white rounded-lg shadow p-4 mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-end"
        >
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">From (UTC)</label>
            <input
              type="date"
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.dateFrom}
              onChange={(e) => setForm((f) => ({ ...f, dateFrom: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">To (UTC)</label>
            <input
              type="date"
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.dateTo}
              onChange={(e) => setForm((f) => ({ ...f, dateTo: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Aircraft</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.aircraft_id}
              onChange={(e) => setForm((f) => ({ ...f, aircraft_id: e.target.value }))}
              disabled={aircraftLoading}
            >
              <option value="">{aircraftLoading ? 'Loading...' : 'All Aircraft'}</option>
              {aircraft.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.registration_number}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Booking status</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.booking_status}
              onChange={(e) => setForm((f) => ({ ...f, booking_status: e.target.value }))}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Cancelled">Cancelled</option>
              <option value="Completed">Completed</option>
              <option value="Refunded">Refunded</option>
              <option value="PendingPJOConfirmation">PendingPJOConfirmation</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Transaction status</label>
            <select
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.transaction_status}
              onChange={(e) => setForm((f) => ({ ...f, transaction_status: e.target.value }))}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Success">Success</option>
              <option value="Failed">Failed</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name, email, token, reference, tail, flight…"
              className="w-full border rounded px-2 py-1.5 text-sm"
              value={form.search}
              onChange={(e) => setForm((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <div className="flex flex-wrap gap-2 xl:col-span-6">
            <button
              type="submit"
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-medium rounded"
            >
              Apply filters
            </button>
            <button
              type="button"
              onClick={handleResetDates}
              className="px-4 py-2 border border-gray-300 text-sm rounded hover:bg-gray-50"
            >
              Last 30 days
            </button>
          </div>
        </form>

        {meta?.note && (
          <p className="text-xs text-gray-500 mb-3 max-w-4xl">{meta.note}</p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Records (filter)</p>
            <p className="text-2xl font-bold text-gray-900">{summary?.record_count ?? '—'}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Paid total (all rows)</p>
            <p className="text-2xl font-bold text-gray-900">
              ${Number(summary?.sum_paid_total_all || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">GMV — Success + Confirmed/Completed</p>
            <p className="text-2xl font-bold text-green-700">
              ${Number(summary?.sum_paid_confirmed_gmv || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Refunded (txn or booking)</p>
            <p className="text-2xl font-bold text-amber-700">
              ${Number(summary?.sum_paid_total_refunded_status || 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-gray-600 text-sm">Cancelled — payment still Success</p>
            <p className="text-2xl font-bold text-slate-700">
              ${Number(summary?.sum_paid_cancelled_captured || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {summary?.counts_by_booking_status && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-2">Counts by booking status</h2>
            <div className="flex flex-wrap gap-2">
              {Object.entries(summary.counts_by_booking_status).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm">
                  <span className="font-medium">{k}</span>
                  <span className="text-gray-600">{v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="text-left px-3 py-2">Reference</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-left px-3 py-2">Route / flight</th>
                <th className="text-left px-3 py-2">Paid</th>
                <th className="text-left px-3 py-2">Booking</th>
                <th className="text-left px-3 py-2">Txn</th>
                <th className="text-left px-3 py-2">Created (UTC)</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No bookings for this filter.
                  </td>
                </tr>
              ) : (
                bookings.map((b) => {
                  const u = b.user;
                  const name = u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '';
                  const paid = Number(b.paid_total_amount || 0);
                  const cur = b.paid_currency || 'USD';
                  return (
                    <tr key={b.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="font-mono text-xs">{b.reference_number}</span>
                      </td>
                      <td className="px-3 py-2">
                        {u ? (
                          <Link
                            className="text-blue-700 hover:underline"
                            to={userProfilePath(u.id, u.user_type)}
                          >
                            {name || u.email}
                          </Link>
                        ) : (
                          <Link className="text-blue-700 hover:underline" to={`/admin/users/${b.user_id}`}>
                            User #{b.user_id}
                          </Link>
                        )}
                        {u?.email && name ? (
                          <div className="text-xs text-gray-500">{u.email}</div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <div>{b.departure_airport_iata} → {b.arrival_airport_iata}</div>
                        <div className="text-xs text-gray-500">{b.flight_number} · {b.registration_number}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {cur} {paid.toFixed(2)}
                      </td>
                      <td className="px-3 py-2">{b.booking_status}</td>
                      <td className="px-3 py-2">{b.transaction_status}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                        {b.created_at ? new Date(b.created_at).toISOString().replace('T', ' ').slice(0, 19) : '—'}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {pagination && (
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              Page {pagination.page} of {totalPages} ({pagination.total} records)
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingManagementPage;
